import { DialogContent, Grid, Stack, Typography, Box, Chip } from "@mui/material";
import CommonDialog from "app/components/common/Dialogs/CommonDialog";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import LabelTextComponent from "app/components/common/LabelTextComponent";
import React from "react";
import { CustomTextField } from "assets/styles/globalStyledComponent";

const ViewTemplateDialog = ({
  open = false,
  onClose = () => {},
  viewData = {},
}) => {
  return (
    <CommonDialog
      open={open}
      onClose={onClose}
      title="View Template Mapping"
      minWidth="1200px"
      maxWidth="1400px"
    >
      <DialogContent sx={{ mt: 2, p: 0 }}>
        <Grid container sx={{ height: "calc(100vh - 200px)", minHeight: "600px" }}>
          {/* Left Panel - Details */}
          <Grid 
            item 
            xs={12} 
            md={6} 
            sx={{ 
              borderRight: "2px solid #e0e0e0",
              p: 3,
              overflowY: "auto",
              backgroundColor: "#fafafa"
            }}
          >
            <Stack spacing={3}>
              {/* Purpose and Template Name in one row */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4.8}>
                  <TextFieldLabel title="Purpose" />
                  <CustomTextField
                    fullWidth
                    size="small"
                    value={viewData?.purpose || ""}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={7.2}>
                  <TextFieldLabel title="Template Name" />
                  <CustomTextField
                    fullWidth
                    size="small"
                    value={viewData?.name || ""}
                    disabled
                  />
                </Grid>
              </Grid>

              {/* Template Info */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <LabelTextComponent
                    label="Type"
                    text={viewData?.type || "--"}
                    backgroundColor
                  />
                </Grid>
                <Grid item xs={6}>
                  <LabelTextComponent
                    label="Parameters"
                    text={viewData?.num_of_parameters || 0}
                    backgroundColor
                  />
                </Grid>
              </Grid>

              {/* Status */}
              <Box>
                <LabelTextComponent
                  label="Status"
                  text={viewData?.active ? "Active" : "Inactive"}
                  backgroundColor
                />
              </Box>

              {/* Variables with Priority */}
              {viewData?.parameters?.length > 0 && (
                <Box>
                  <TextFieldLabel title="Variables with Priority" />
                  <Stack spacing={1.5} sx={{ mt: 1 }}>
                    {viewData.parameters.map((param, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          padding: "12px",
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <Chip
                          label={index + 1}
                          size="small"
                          sx={{
                            backgroundColor: "#1976d2",
                            color: "#fff",
                            fontWeight: 600,
                            minWidth: "32px",
                            fontSize: "12px",
                          }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 600,
                              color: "#1976d2",
                            }}
                          >
                            {`{{${index + 1}}}`} → {param}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "11px",
                              color: "#666",
                              fontStyle: "italic",
                            }}
                          >
                            Priority: {index + 1}
                            {index === 0 ? "st" : index === 1 ? "nd" : index === 2 ? "rd" : "th"}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Grid>

          {/* Right Panel - Template Preview */}
          <Grid 
            item 
            xs={12} 
            md={6} 
            sx={{ 
              p: 3,
              overflowY: "auto",
              // backgroundColor: "#fff"
            }}
          >
            <Box>
              <Box
                sx={{
                  // backgroundColor: "#e5ddd5",
                  borderRadius: "12px",
                  p: 2,
                  maxWidth: "400px",
                  margin: "0 auto",
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "10px",
                    padding: "12px",
                    boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                    border: "1px solid #f3f3f3",
                    position: "relative",
                  }}
                >
                  {/* Image Preview for IMAGE type */}
                  {viewData?.type === "IMAGE" && viewData?.link && (
                    <Box sx={{ mb: 2, position: "relative" }}>
                      <Box
                        component="img"
                        src={viewData.link}
                        alt="Template preview"
                        sx={{
                          width: "100%",
                          height: "200px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          display: "block",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      <Box
                        sx={{
                          width: "100%",
                          height: "200px",
                          backgroundColor: "#f0f0f0",
                          borderRadius: "8px",
                          display: "none",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "2px dashed #ccc",
                        }}
                      >
                        <Typography sx={{ color: "#999" }}>
                          Invalid image URL
                        </Typography>
                      </Box>
                      <Chip
                        label="IMAGE"
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          backgroundColor: "rgba(0,0,0,0.7)",
                          color: "#fff",
                          fontWeight: 600,
                          zIndex: 1,
                        }}
                      />
                    </Box>
                  )}

                  {/* Template Content */}
                  <Typography
                    sx={{
                      fontSize: "14px",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      color: "#000",
                      lineHeight: 1.5,
                    }}
                  >
                    {viewData?.template || "No template content available"}
                  </Typography>

                  {/* Template Info Footer */}
                  <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #eee" }}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography sx={{ fontSize: "11px", color: "#666" }}>
                          Type: <strong>{viewData?.type || "--"}</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography sx={{ fontSize: "11px", color: "#666" }}>
                          Parameters: <strong>{viewData?.num_of_parameters || 0}</strong>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>

                {/* Variable Mapping Info */}
                {viewData?.parameters?.length > 0 && (
                  <Box  sx={{ mt: 2 ,
                      borderRadius: "10px",
                      padding: "12px",
                      boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                      border: "1px solid #f3f3f3", }}>
                    <Typography 
                      sx={{ 
                        fontSize: "12px", 
                        fontWeight: 600, 
                        mb: 1,
                        color: "#555"
                      }}
                    >
                      Variable Mapping:
                    </Typography>
                    <Stack spacing={0.5}>
                      {viewData.parameters.map((param, index) => (
                        <Box
                          key={index}
                          sx={{
                            backgroundColor: "#fff",
                            padding: "8px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Chip
                            label={`{{${index + 1}}}`}
                            size="small"
                            sx={{
                              backgroundColor: "#dcf8c6",
                              fontSize: "11px",
                              height: "20px",
                            }}
                          />
                          <Typography sx={{ fontSize: "12px", color: "#666" }}>
                            = {param}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </CommonDialog>
  );
};

export default ViewTemplateDialog;
